import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../app/store';
import { backToDay, discardShare, openZones, setShare, ui } from '../app/ui';
import * as act from '../app/actions';
import { inputOf, mostOf, sharedNow, shown, useEngine, type LeftOut, type Shown } from '../app/sharing';
import { inMinutes, length, plural, two } from '../app/words';
import { track } from '../data/analytics';
import type { Model } from '../data/model';
import type { Balance } from '../data/share';
import { Av, More } from '../ui/bits';
import { Icon } from '../ui/Icon';
import { toast } from '../ui/Overlays';
import { vehicleWords } from './Live';

/**
 * Share out (Spoke's Optimise with chosen drivers, fairer): waiting stops shared across couriers in the list beside the map.
 * The owner picks who takes them (or as few as needed), the balance (even stops, even time, fastest) and each courier's
 * most stops; the engine (src/data/share.ts) answers on straight lines, each route drawn in its courier's colour. Stops move
 * by hand between couriers (drag, or right-click Move to). Sort all asks Google; Send all, or one at a time, sends each as
 * a normal route. Every stop left out says why, with a way to fix it.
 */

const BALANCES: { k: Balance; l: string; icon: string; tip: string }[] = [
  { k: 'stops', l: 'Even stops', icon: 'scales', tip: 'Each courier gets as many stops as the others, within one' },
  { k: 'time', l: 'Even time', icon: 'clock', tip: 'Each courier’s day about as long as the others’' },
  { k: 'fastest', l: 'Fastest', icon: 'lightning', tip: 'The least driving overall: some may get more stops than others' },
];

/** "about 1 h 40 min" for [s] seconds. */
const about = (s: number) => `about ${inMinutes(s * 1000)}`;

export function ShareView({ m }: { m: Model }) {
  const s = useStore(ui);
  const share = s.share!;
  const { input, stops, unplaced } = useMemo(() => inputOf(m, share), [m.waiting, m.zones, m.people, m.shop, share.ids, share.couriers, share.fewest, share.balance, share.max]);
  const result = useEngine(input);
  const view = useMemo(() => (result ? shown(m, share, result, stops, unplaced) : null), [result, share.moved, share.sorted, share.fewest, stops, unplaced, m.people, m.zones, m.shop]);
  useEffect(() => sharedNow.set({ view }), [view]);
  useEffect(() => () => sharedNow.set({ view: null }), []);
  const [sorting, setSorting] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  useShareDrag(list);
  const picked = share.couriers.map((u) => m.person(u)).filter((p) => !!p);
  const routes = view?.routes ?? [];
  const sending = routes.filter((r) => r.ids.length);
  const n = stops.length;
  const sort = async () => {
    setSorting(true);
    try {
      await act.sortShared();
    } finally {
      setSorting(false);
    }
  };
  return (
    <>
      <div className="lhead">
        <div className="row">
          <button className="iconbtn sm" data-tip="Back to today · Esc" aria-label="Back" onClick={backToDay}>
            <Icon name="arrowLeft" size="lg" />
          </button>
          <span className="title grow">Share out</span>
          <button className="btn sm" onClick={() => discardShare((msg, undo) => toast(msg, { undo: () => void undo() }))}>
            Discard
          </button>
        </div>
        <div className="cap" style={{ marginTop: 4 }}>
          {plural(n, 'stop')} waiting{share.ids.length > n ? ` · ${share.ids.length - n} sent or removed meanwhile` : ''}. Nothing is sent until you send it.
        </div>
      </div>

      <div className="lsec">
        <span className="eyebrow">Who takes them</span>
      </div>
      <div className="sharepad">
        <div className="seg wide2" role="group" aria-label="Who takes them">
          <button aria-pressed={!share.fewest} data-tip="Each courier you tick gets a share" onClick={() => setShare(() => ({ fewest: false }))}>
            <Icon name="users" />
            The couriers I pick
          </button>
          <button aria-pressed={share.fewest} data-tip="Only as many of those you tick as their most stops need" onClick={() => setShare(() => ({ fewest: true }))}>
            <Icon name="usersThree" />
            As few as needed
          </button>
        </div>
      </div>
      <div className="rows sharepeople">
        {m.people.map((p) => {
          const on = share.couriers.includes(p.uid);
          const most = mostOf(share, p);
          return (
            <div key={p.uid} className={`item${on ? '' : ' off'}`} data-ctx={`sharecourier:${p.uid}`}>
              <button className={`cbx${on ? ' on' : ''}`} role="checkbox" aria-checked={on} aria-label={`${on ? 'Leave out' : 'Pick'} ${p.first}`} onClick={() => setShare((x) => ({ couriers: on ? x.couriers.filter((u) => u !== p.uid) : [...x.couriers, p.uid] }))}>
                {on && <Icon name="check" />}
              </button>
              <Av p={p} size="sm" />
              <div className="grow" style={{ minWidth: 0 }}>
                <b>{p.first}</b>
                <div className="cap ellipsis">
                  {vehicleWords(p.courier.vehicle)}
                  {p.day?.running ? ' · on a route: this waits until it’s done' : ''}
                </div>
              </div>
              <label className="most" data-tip={share.max[p.uid] == null ? `What a ${vehicleWords(p.courier.vehicle).toLowerCase()} carries` : 'Set by you'}>
                <span className="cap">Most</span>
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={most}
                  aria-label={`${p.first}’s most stops`}
                  onChange={(e) => {
                    const v = Math.round(Number(e.target.value));
                    if (v >= 1 && v <= 500) setShare((x) => ({ max: { ...x.max, [p.uid]: v } }));
                  }}
                />
              </label>
            </div>
          );
        })}
      </div>

      <div className="lsec">
        <span className="eyebrow">Balance</span>
      </div>
      <div className="sharepad">
        <div className="seg wide3" role="group" aria-label="Balance">
          {BALANCES.map((b) => (
            <button key={b.k} aria-pressed={share.balance === b.k} data-tip={b.tip} onClick={() => setShare(() => ({ balance: b.k }))}>
              <Icon name={b.icon} />
              {b.l}
            </button>
          ))}
        </div>
      </div>

      <div className="lsec">
        <span className="eyebrow">Routes</span>
        <span className="cnt">{sending.length}</span>
        <span className="grow" />
        {sending.length > 0 && (
          <button className="mini" disabled={sorting} data-tip="Google orders and times each route, through Routr’s server: one request per 25 stops" onClick={() => void sort()}>
            <Icon name="path" size="sm" />
            {sorting ? 'Sorting…' : sending.every((r) => r.google) ? 'Sorted' : 'Sort all'}
          </button>
        )}
      </div>
      {view ? <Summary m={m} routes={routes} crossings={view.crossings} left={view.left.length} total={n} /> : <div className="cap sharesum">Sharing out…</div>}
      <div className="sharelist" ref={list}>
        {routes.map((r) => (
          <RouteRow key={r.uid} m={m} r={r} open={share.open === r.uid} most={mostOf(share, r.p)} />
        ))}
        {!picked.length && <div className="empty cap">Tick who takes them above.</div>}
      </div>

      {view && view.left.length > 0 && (
        <>
          <div className="lsec">
            <span className="eyebrow">Left out</span>
            <span className="cnt">{view.left.length}</span>
          </div>
          <div className="rows">
            {view.left.map((x) => (
              <LeftRow key={x.w.id} m={m} x={x} />
            ))}
          </div>
        </>
      )}

      <div className="sharefoot">
        <button className="btn primary" style={{ width: '100%' }} disabled={!sending.length} onClick={() => void act.sendShared()}>
          <Icon name="paperPlaneTilt" />
          {sending.length > 1 ? `Send all · ${plural(sending.length, 'route')}` : sending.length ? `Send to ${sending[0].p.first}` : 'Nothing to send yet'}
        </button>
        <div className="cap" style={{ marginTop: 8, textAlign: 'center' }}>
          {sending.every((r) => r.google) && sending.length ? 'Google’s order and times. ' : 'Times by straight lines at 30 km/h and 3 minutes a stop; Google orders and times each route as it’s sent. '}
          Each waits on its courier’s map until they start it.
        </div>
      </div>
    </>
  );
}

/** "63 stops → 5 couriers · 12 to 13 stops each · zones kept · no routes cross". */
function Summary({ m, routes, crossings, left, total }: { m: Model; routes: Shown[]; crossings: number; left: number; total: number }) {
  const s = useStore(ui);
  const share = s.share!;
  const used = routes.filter((r) => r.ids.length);
  if (!used.length) return <div className="cap sharesum">{total ? 'No courier takes any yet.' : 'No stops to share out.'}</div>;
  const counts = used.map((r) => r.ids.length);
  const days = used.map((r) => r.totalS);
  const lo = Math.min(...counts);
  const hi = Math.max(...counts);
  const against = routes.reduce((a, r) => a + r.warn.size, 0);
  const parts = [
    `${plural(total - left, 'stop')} → ${plural(used.length, 'courier')}`,
    share.balance === 'time' ? `days ${about(Math.min(...days)).replace('about ', '')} to ${about(Math.max(...days)).replace('about ', '')}` : lo === hi ? `${plural(lo, 'stop')} each` : `${lo} to ${hi} stops each`,
    m.zones.some((z) => z.couriers.length) ? (against ? `${plural(against, 'stop')} moved against a zone` : 'zones kept') : '',
    crossings ? `routes cross ${crossings === 1 ? 'once' : `${crossings} times`}` : 'no routes cross',
  ].filter(Boolean);
  return <div className="cap sharesum">{parts.join(' · ')}</div>;
}

function RouteRow({ m, r, open, most }: { m: Model; r: Shown; open: boolean; most: number }) {
  const s = useStore(ui);
  const sel = [...s.sel].filter((k) => k.startsWith('share:')).map((k) => k.slice(6));
  return (
    <div className={`srow${open ? ' on' : ''}`} style={{ '--c': r.p.hex, '--on': r.p.on } as React.CSSProperties} data-ctx={`sharecourier:${r.uid}`} data-share={r.uid}>
      <div className="who" role="button" tabIndex={0} aria-expanded={open} onClick={() => setShare((x) => ({ open: x.open === r.uid ? null : r.uid }))}>
        <Av p={r.p} />
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="nm">{r.p.first}</div>
          <div className="meta" data-tip={r.ids.length ? (r.google ? 'Google’s order and times, and 3 minutes at each stop' : 'By straight lines at 30 km/h, and 3 minutes at each stop') : undefined}>
            {r.ids.length ? (
              <>
                {plural(r.ids.length, 'stop')}
                <span className="dotb">{length(r.distanceM)}</span>
                <span className="dotb">{about(r.totalS)}</span>
              </>
            ) : (
              'Nothing for them in this share'
            )}
          </div>
        </div>
        <More menu={`sharecourier:${r.uid}`} />
      </div>
      <div className="sbar" aria-hidden="true">
        <i style={{ width: `${Math.min(100, (r.ids.length / Math.max(1, most)) * 100)}%` }} />
      </div>
      <div className="row smost">
        <span className="cap grow">
          {r.ids.length} of {most} most{r.warn.size ? ` · ${plural(r.warn.size, 'stop')} outside their zones` : ''}
        </span>
        {r.ids.length > 0 && (
          <button className="mini" onClick={() => void act.sendShared([r.uid])}>
            <Icon name="paperPlaneTilt" size="sm" />
            Send to {r.p.first}
          </button>
        )}
      </div>
      {open && (
        <div className="stops sstops">
          {r.ids.map((id, i) => {
            const w = m.waiting.find((x) => x.id === id);
            if (!w) return null;
            const on = s.sel.has(`share:${id}`);
            return (
              <div
                key={id}
                className={`stop${on ? ' picked' : ''}`}
                data-ctx={`sharestop:${id}`}
                data-sdrag={on && sel.length > 1 ? sel.join(',') : id}
                onClick={() =>
                  ui.set((x) => {
                    const next = new Set(x.sel);
                    if (next.has(`share:${id}`)) next.delete(`share:${id}`);
                    else next.add(`share:${id}`);
                    return { sel: next };
                  })
                }
              >
                <span className="n">{two(i + 1)}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="a ellipsis">{w.street}</div>
                  <div className="s ellipsis">{[w.area, w.name, w.type === 'pickup' ? 'Pickup' : null].filter(Boolean).join(' · ')}</div>
                  {r.warn.get(id) && (
                    <span className="chip cau" style={{ marginTop: 4 }}>
                      <Icon name="warning" size="sm" />
                      {r.warn.get(id)}
                    </span>
                  )}
                </div>
                <span className="drag" aria-hidden="true" data-tip="Drag to another courier">
                  <Icon name="dotsSixVertical" />
                </span>
              </div>
            );
          })}
          {sel.length > 0 && (
            <div className="selbar">
              <b>{plural(sel.length, 'stop')} picked</b>
              <button className="mini" data-menu={`sharemove:${sel.join(',')}`}>
                Move to
                <Icon name="caretDown" size="sm" />
              </button>
              <button className="link cap" onClick={() => ui.set({ sel: new Set() })}>
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** A stop left out: why, naming the courier or the rule, and how to fix it from here. */
function LeftRow({ m, x }: { m: Model; x: LeftOut }) {
  const share = useStore(ui).share!;
  const { words, fixes } = leftWords(m, x, share.couriers);
  return (
    <div className="item look" data-ctx={`sharestop:${x.w.id}`}>
      <span className="kdisc" style={{ '--tone': 'var(--caution)' } as React.CSSProperties}>
        <Icon name="warningCircle" size="sm" />
      </span>
      <div className="grow" style={{ minWidth: 0 }}>
        <b className="ellipsis" style={{ display: 'block' }}>
          {x.w.street}
        </b>
        <div className="cap">{words}</div>
        <div className="acts">
          {fixes.map((f) => (
            <button key={f.label} className="mini" onClick={f.run}>
              {f.label}
            </button>
          ))}
          <button className="mini" data-menu={`sharemove:${x.w.id}`}>
            Give it to
            <Icon name="caretDown" size="sm" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Why a stop is left out, in words, and the ways to fix it (menus use the same). */
export function leftWords(m: Model, x: LeftOut, picked: string[]): { words: string; fixes: { label: string; run: () => void }[] } {
  const why = x.why;
  const first = (uid: string) => m.person(uid)?.first ?? 'a courier who has left';
  const pick = (uid: string) => () => setShare((s) => ({ couriers: [...s.couriers, uid] }));
  // Each zone the reason names, to open from here; the share stays as it is meanwhile. By name, as the reason has them.
  const zoneLinks = (names: string[]) => {
    const links = [...new Set(names)].flatMap((name) => {
      const z = m.zones.find((x) => x.name === name);
      return z ? [{ label: `Open ${name}`, run: () => (openZones(z.id), track('share_zone_opened', { why: why.kind })) }] : [];
    });
    return links.length || !names.length ? links : [{ label: 'Open Zones', run: () => openZones() }];
  };
  switch (why.kind) {
    case 'unplaced':
      return { words: 'Not on the map yet: Google’s position for it has gone (kept 29 days at most), or it was never found.', fixes: [{ label: 'Find it on the map', run: () => void act.findWaitingAgain([x.w.id]) }] };
    case 'zone': {
      const who = why.couriers.filter((u) => m.person(u));
      const names = who.map(first);
      const zones = why.zones.join(' and ');
      return {
        words: who.length ? `In ${zones}’s zone: ${names.length === 1 ? `${names[0]} isn’t` : `${names.join(' and ')} aren’t`} picked today.` : `In ${zones}’s zone, and none of its couriers is in your business now.`,
        fixes: [...who.map((u) => ({ label: `Pick ${first(u)}`, run: pick(u) })), ...zoneLinks(why.zones)],
      };
    }
    case 'full': {
      const others = m.people.filter((p) => !picked.includes(p.uid));
      return {
        words: `Over ${first(why.uid)}’s ${why.max} stops${why.zones.length ? `, and only ${first(why.uid)} covers ${why.zones.join(' and ')}’s zone among those picked` : ''}.`,
        fixes: [
          { label: `Raise ${first(why.uid)}’s most to ${why.max + 5}`, run: () => setShare((s) => ({ max: { ...s.max, [why.uid]: why.max + 5 } })) },
          ...(others.length && !why.zones.length ? [{ label: `Pick ${others[0].first} too`, run: pick(others[0].uid) }] : []),
          ...zoneLinks(why.zones),
        ],
      };
    }
    case 'apart':
      return { words: `It goes with ${m.waiting.find((w) => w.id === why.other)?.street ?? 'another stop'} (a pickup and its delivery), and their zones have no courier in common.`, fixes: zoneLinks(why.zones) };
    case 'nobody':
      return { words: 'No courier picked.', fixes: m.people.length ? [{ label: 'Pick everyone', run: () => setShare(() => ({ couriers: m.people.map((p) => p.uid) })) }] : [] };
  }
}

/**
 * Drag a stop (or the stops picked) onto another courier's row: a copy rides under the pointer, the row it would land on is
 * outlined, letting go moves them there (as the app's own drag of a stop, drag.ts). Esc cancels; a press that doesn't move
 * stays a click.
 */
function useShareDrag(box: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let d: { ids: string[]; row: HTMLElement; x0: number; y0: number; card?: HTMLElement; over?: HTMLElement | null } | null = null;
    let swallow = false;
    const end = (move: boolean) => {
      if (!d) return;
      const { ids, over, card } = d;
      card?.remove();
      over?.classList.remove('drop');
      document.body.classList.remove('dragging');
      d = null;
      const uid = over?.dataset.share;
      if (move && uid) act.moveShared(ids, uid);
    };
    const down = (e: PointerEvent) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>('[data-sdrag]');
      if (!row || e.button !== 0) return;
      d = { ids: row.dataset.sdrag!.split(',').filter(Boolean), row, x0: e.clientX, y0: e.clientY };
    };
    const move = (e: PointerEvent) => {
      if (!d) return;
      if (!d.card) {
        if (Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 5) return;
        const r = d.row.getBoundingClientRect();
        const card = d.row.cloneNode(true) as HTMLElement;
        card.classList.add('dragcard');
        if (d.ids.length > 1) card.querySelector('.a')!.textContent = `${d.ids.length} stops`;
        Object.assign(card.style, { width: `${r.width}px`, left: `${r.left}px`, top: `${r.top}px` });
        document.body.appendChild(card);
        document.body.classList.add('dragging');
        d.card = card;
      }
      const r = d.row.getBoundingClientRect();
      d.card.style.transform = `translate(${e.clientX - d.x0}px, ${e.clientY - d.y0}px)`;
      void r;
      const at = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>('[data-share]') ?? null;
      const home = d.row.closest<HTMLElement>('[data-share]');
      const over = at && at !== home ? at : null;
      if (over !== d.over) {
        d.over?.classList.remove('drop');
        over?.classList.add('drop');
        d.over = over;
      }
    };
    const up = () => {
      if (d?.card) swallow = true;
      end(true);
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && d?.card && (e.stopPropagation(), end(false));
    const click = (e: MouseEvent) => {
      if (swallow) {
        e.stopPropagation();
        e.preventDefault();
        swallow = false;
      }
    };
    el.addEventListener('pointerdown', down);
    addEventListener('pointermove', move);
    addEventListener('pointerup', up);
    addEventListener('keydown', key, true);
    el.addEventListener('click', click, true);
    return () => {
      el.removeEventListener('pointerdown', down);
      removeEventListener('pointermove', move);
      removeEventListener('pointerup', up);
      removeEventListener('keydown', key, true);
      el.removeEventListener('click', click, true);
      end(false);
    };
  }, [box]);
}

